import { CircularProgress } from "@mui/material";
import { AxiosResponse } from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import { Accordion, Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import ReactSelect, { SingleValue, StylesConfig, Theme } from "react-select";
import Swal from "sweetalert2";

import Button from "components/Button";
import ContainerAnimation from "components/ContainerAnimation";
import FormatGroupLabel from "components/FormatGroupLabel";
import LinkButton from "components/LinkButton";
import LoadingScreen from "components/LoadingScreen";
import ModalBodyComponentStepper from "components/ModalBodyComponentStepper";
import ModalComponent from "components/ModalComponent";
import RequiredDocuments from "components/RequiredDocuments";
import PatientQuoteEditForm from "pages/Quotes/PatientQuote/components/Forms/PatientQuoteEditForm";
import ProfessionalQuoteEditForm from "pages/Quotes/ProfessionalQuote/components/Forms/ProfessionalQuoteEditForm";
import QuoteEditForm from "pages/Quotes/ProfessionalQuote/components/Forms/QuoteEditForm";
import SecretaryQuoteEditForm from "pages/Quotes/ProfessionalQuote/components/Forms/SecretaryQuoteEditForm";
import { useAuth } from "providers/Auth";
import { useQuoteDetails } from "providers/QuoteDetails";
import { api } from "services";
import {
  PaymentFeeOwner,
  PaymentMethod,
  PaymentMethodDescription,
  QuoteActions,
  QuoteStatus,
  quoteStatusColor,
  quoteStatusTitle,
  RoleDescription,
  PaymentLinkAction,
  FormTypeDescription,
  ProcedureDateStatusDescription,
  QuotePaymentStatusDescription,
} from "types/enums";
import { DatabaseFile, FileAttachment } from "types/files";
import { ActionFormQuote, FormQuote } from "types/formQuote";
import { GroupedOption, Quote, StatusOption } from "types/quotes";
import { formatAsPercent, formatPrice } from "utils/formats";
import {
  dateStatusOptions,
  defaultFormTypeOption,
  formTypeOptions,
  formTypeOptionsInterface,
  quotePaymentStatusOptions,
} from "utils/selectOptions";
import { getSplitBeneficiaries } from "utils/splitBeneficiaries";
import { toast } from "utils/toast";

import AuditLogList from "./components/AuditLogList";
import Chat from "./components/Chat";
import Checklist from "./components/Checklist/Checklist";
import CustomToggle from "./components/CustomToggle";
import FileAddForm from "./components/FileAddForm";
import FileCard from "./components/FileCard";
import FormQuoteCard from "./components/FormQuoteCard";
import Step1PersonalInfoForm from "./components/FormQuoteCard/components/Form/Step1PersonalInfoForm";
import Step2AddessInfoForm from "./components/FormQuoteCard/components/Form/Step2AdressInfoForm";
import Step3ProfessionalInfoForm from "./components/FormQuoteCard/components/Form/Step3ProfessionalInfoForm";
import Step4OthersInfoForm from "./components/FormQuoteCard/components/Form/Step4OthersInfoForm";
import PaymentLink from "./components/PaymentLink";
import TermsLink from "./components/TermsLink";
import TimeLineStep from "./components/TimeLineStep";
import * as S from "./styles";

interface SplitItem {
  name: string;
  pixKey: string;
  value: string;
}

const parseSplitBRL = (v: string) =>
  Number((v || "0").replace(/\./g, "").replace(",", ".")) || 0;

const formatSplitBRL = (raw: string) => {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  return (Number(digits) / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
};

const customTheme = (theme: Theme) => ({
  ...theme,
  borderRadius: 7,
  colors: {
    ...theme.colors,
    /*
     * multiValue(remove)/color:hover
     */
    danger: "var(--danger)",
    /*
     * multiValue(remove)/backgroundColor(focused)
     * multiValue(remove)/backgroundColor:hover
     */
    dangerLight: "var(--danger-light)",
    /*
     * control/backgroundColor
     * menu/backgroundColor
     * option/color(selected)
     */
    neutral0: "var(--neutral-0)",
    /*
     * control/backgroundColor(disabled)
     */
    neutral5: "var(--neutral-5)",
    /*
     * control/borderColor(disabled)
     * multiValue/backgroundColor
     * indicators(separator)/backgroundColor(disabled)
     */
    neutral10: "var(--neutral-10)",
    /*
     * control/borderColor
     * option/color(disabled)
     * indicators/color
     * indicators(separator)/backgroundColor
     * indicators(loading)/color
     */
    neutral20: "var(--neutral-20)",
    /*
     * control/borderColor(focused)
     * control/borderColor:hover
     */
    neutral30: "var(--neutral-30)",
    /*
     * menu(notice)/color
     * singleValue/color(disabled)
     * indicators/color:hover
     */
    neutral40: "var(--neutral-40)",
    /*
     * placeholder/color
     */
    neutral50: "var(--neutral-50)",
    /*
     * indicators/color(focused)
     * indicators(loading)/color(focused)
     */
    neutral60: "var(--neutral-60)",
    neutral70: "var(--neutral-70)",
    /*
     * input/color
     * multiValue(label)/color
     * singleValue/color
     * indicators/color(focused)
     * indicators/color:hover(focused)
     */
    neutral80: "var(--neutral-80)",
    neutral90: "var(--neutral-90)",
    /*
     * control/boxShadow(focused)
     * control/borderColor(focused)
     * control/borderColor:hover(focused)
     * option/backgroundColor(selected)
     * option/backgroundColor:active(selected)
     */
    primary: "var(--primary)",
    /*
     * option/backgroundColor(focused)
     */
    primary25: "var(--primary-25)",
    /*
     * option/backgroundColor:active
     */
    primary50: "var(--primary-50)",
    primary75: "var(--primary-75)",
  },
});

const customStyles: StylesConfig = {
  control: (base: Record<string, unknown>) => ({
    ...base,
    height: 48,
    borderColor: "var(--border)",
    cursor: "pointer",
    boxShadow: "none",
    "&:hover": {
      borderColor: "var(--border)",
    },
  }),
  singleValue: (base: Record<string, unknown>) => ({
    ...base,
    textTransform: "uppercase",
  }),
  option: (base: Record<string, unknown>, state: any) => ({
    ...base,
    cursor: "pointer",
    textTransform: "uppercase",
    backgroundColor: state.isSelected ? "var(--primary)" : "inherit",
    color: state.isSelected ? "var(--white)" : "inherit",
    "&:hover": {
      backgroundColor: state.isSelected ? "var(--primary)" : "var(--light)",
    },
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 100,
    background: "var(--white)",
    color: "var(--text)",
    border: "1px solid var(--border)",
  }),
  dropdownIndicator: (base: Record<string, unknown>) => ({
    ...base,
    "& svg": {
      width: 15,
      color: "var(--text)",
    },
  }),
};

// Backend ainda não tem endpoint pra salvar o split por orçamento —
// "Salvar Split" hoje só mostra um toast e não persiste nada. Oculta até existir.
const SHOW_SPLIT_PAGAMENTO = false;

const QuoteDetails = () => {
  const { userRole } = useAuth();
  const location = useLocation();

  if (!location.state) {
    return (
      <Navigate
        to={
          userRole === RoleDescription.Patient
            ? "/orcamentos-paciente"
            : "/orcamentos-profissional"
        }
      />
    );
  }

  const quoteId = location.state as number;
  const navigate = useNavigate();
  const [quote, setQuote] = useState<Quote>({} as Quote);
  const [formModalShow, setFormModalShow] = useState(false);
  const [paymentLinkModalShow, setPaymentLinkModalShow] = useState(false);
  // Ids das tabelas de máquina física (TerminalPDQ) — não geram link de pagamento.
  const [machineTableIds, setMachineTableIds] = useState<number[]>([]);
  const [termsLinkModalShow, setTermsLinkModalShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [quoteStatusSelected, setQuoteStatusSelected] = useState<StatusOption>(
    {} as StatusOption
  );
  const [databaseFiles, setDatabaseFiles] = useState<DatabaseFile[]>([]);
  const [dateStatusSelected, setDateStatusSelected] = useState<
    Omit<StatusOption, "statusColor">
  >({} as Omit<StatusOption, "statusColor">);
  const [paymentStatusSelected, setPaymentStatusSelected] = useState<
    Omit<StatusOption, "statusColor">
  >({} as Omit<StatusOption, "statusColor">);
  const [selectFormTypeOptions, setSelectFinancingOptions] =
    useState<formTypeOptionsInterface[]>(formTypeOptions);
  const [selectedFormTypeOption, setSelectedFormTypeOption] =
    useState<formTypeOptionsInterface>(defaultFormTypeOption);
  const [formQuoteModalShow, setFormQuoteModalShow] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [formQuote, setFormQuote] = useState<FormQuote>({} as FormQuote);
  const [splitItems, setSplitItems] = useState<SplitItem[]>([
    { name: "", pixKey: "", value: "" },
  ]);
  const splitTotal = splitItems.reduce(
    (sum, it) => sum + parseSplitBRL(it.value),
    0
  );
  const splitHasData = splitItems.some((it) => it.name || it.pixKey || it.value);
  const splitQuoteValue = quote.payment?.value ?? 0;
  const splitIsValid = Math.abs(splitTotal - splitQuoteValue) < 0.01;

  // Monta o split a partir dos beneficiários pré-cadastrados da empresa
  const buildSplitFromCompany = (): SplitItem[] | null => {
    const companyId = quote.company?.id;
    if (!companyId || splitQuoteValue <= 0) return null;
    const beneficiaries = getSplitBeneficiaries(companyId);
    if (beneficiaries.length === 0) return null;
    return beneficiaries.map((b) => ({
      name: b.name,
      pixKey: b.pixKey,
      value: ((splitQuoteValue * b.percent) / 100).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    }));
  };

  // Pré-preenche o split com o cadastro da empresa (só se ainda intocado)
  useEffect(() => {
    const prefill = buildSplitFromCompany();
    if (!prefill) return;
    setSplitItems((prev) => {
      const pristine =
        prev.length === 1 && !prev[0].name && !prev[0].pixKey && !prev[0].value;
      return pristine ? prefill : prev;
    });
  }, [quote.company?.id, splitQuoteValue]);

  const {
    getFormQuote,
    formQuoteForm,
    setSaveFormAsDraft,
    pendingItems,
    getQuotePendingItems,
  } = useQuoteDetails();

  const groupedOptions: readonly GroupedOption[] = [
    {
      label: "Retornar status para:",
      options: quote?.previusStep?.map((step) => {
        return {
          value: step,
          label: quoteStatusTitle.get(step) as string,
          statusColor: `var(--${quoteStatusColor.get(step)})`,
        };
      }),
    },
    {
      label: "Avançar status para:",
      options: quote?.nextStep?.map((step) => {
        return {
          value: step,
          label: quoteStatusTitle.get(step) as string,
          statusColor: `var(--${quoteStatusColor.get(step)})`,
        };
      }),
    },
  ];

  const selectorStatusCustomStyles = {
    container: (base: Record<string, unknown>) => ({
      ...base,
      padding: 0,
    }),
    control: (base: Record<string, unknown>) => ({
      ...base,
      minHeight: 18,
      fontSize: "80%",
      borderColor: "var(--border)",
      cursor: "pointer",
      boxShadow: "none",
      "&:hover": {
        borderColor: "var(--border)",
      },
    }),
    menu: (base: Record<string, unknown>) => ({
      ...base,
      width: 120,
      fontSize: "80%",
      background: "var(--white)",
      color: "var(--text)",
      border: "1px solid var(--border)",
    }),
    option: (base: Record<string, unknown>, state: any) => ({
      ...base,
      cursor: "pointer",
      fontWeight: "bold",
      backgroundColor: state.isSelected ? "var(--primary)" : "inherit",
      color: state.isSelected ? "var(--white)" : "inherit",
      "&:hover": {
        backgroundColor: state.isSelected ? "var(--primary)" : "var(--light)",
      },
    }),
    singleValue: (base: Record<string, unknown>) => ({
      ...base,
      fontWeight: "bold",
    }),
    dropdownIndicator: (base: Record<string, unknown>) => ({
      ...base,
      padding: "2px",
      "& svg": {
        width: 15,
        color: "var(--text)",
      },
    }),
  };

  const selectorQuoteStatusCustomStyles = (value: StatusOption) => ({
    container: (base: Record<string, unknown>) => ({
      ...base,
      fontSize: "14px",
    }),
    control: (base: Record<string, unknown>) => ({
      ...base,
      borderRadius: 50,
      cursor: "pointer",
      background: value?.statusColor,
      paddingLeft: 8,
      paddingRight: 8,
      border: value?.statusColor,
      boxShadow: "none",
      "&:hover": {
        borderColor: value?.statusColor,
      },
    }),
    menu: (base: Record<string, unknown>) => ({
      ...base,
      maxWidth: 180,
      zIndex: 1030,
      background: "var(--white)",
      color: "var(--text)",
      border: "1px solid var(--border)",
    }),
    option: (base: Record<string, unknown>, state: any) => ({
      ...base,
      cursor: "pointer",
      fontWeight: "bold",
      color: state.data.statusColor,
      backgroundColor: state.isSelected ? "var(--primary)" : "inherit",
      "&:hover": {
        backgroundColor: state.isSelected ? "var(--primary)" : "var(--light)",
      },
    }),
    singleValue: (base: Record<string, unknown>) => ({
      ...base,
      fontWeight: "700",
      color: "var(--white)",
      fontSize: "11px",
      letterSpacing: ".05rem",
    }),
    indicatorsContainer: (base: Record<string, unknown>) => ({
      ...base,
      display: value.value === QuoteStatus.Finished ? "none" : "flex",
    }),
    dropdownIndicator: (base: Record<string, unknown>) => ({
      ...base,
      "& svg": {
        width: 15,
        color: "var(--white)",
      },
    }),
  });

  const handleFinancingChange = (newValue: {
    value: number;
    label: string;
  }) => {
    setSelectedFormTypeOption(newValue);

    if (newValue) {
      setFormQuote({ form: { type: newValue.value } } as FormQuote);
    }
  };

  const closeFormQuoteStepper = () => {
    Swal.fire({
      title: "Deseja salvar como rascunho?",
      icon: "warning",
      position: "center",
      showCloseButton: true,
      showDenyButton: true,
      showConfirmButton: true,
      confirmButtonText: "Salvar",
      denyButtonText: "Não Salvar",
      reverseButtons: true,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-outline-success ml-2",
        denyButton: "btn btn-outline-danger",
      },
    }).then((res) => {
      if (res.isConfirmed) {
        setSaveFormAsDraft(true);
        setFormQuoteModalShow(false);
        setActiveStep(0);

        toast.fire("Formulário foi salvo como rascunho!", "", "success");
      } else if (res.isDenied) {
        setSaveFormAsDraft(false);
        setFormQuoteModalShow(false);
        setActiveStep(0);

        toast.fire("Formulário não foi salvo como rascunho!", "", "info");
      } else {
        return;
      }
    });
  };

  const changeQuoteStatus = async (
    newStatus: SingleValue<{
      value: number;
      label: string;
      statusColor: string;
    }>
  ) => {
    setQuoteStatusSelected(newStatus as StatusOption);

    try {
      await api.patch(`/Quote/${quote.id}/status`, null, {
        params: {
          newStatus: newStatus?.value,
        },
      });

      setIsLoading(true);
      const response = await api.get(`/Quote/${quoteId}`);
      await getQuotePendingItems(quoteId);
      console.log(response.data);

      setIsLoading(false);
      setQuote(response.data);

      toast.fire({
        icon: "success",
        title: "Status atualizado com sucesso!",
      });
    } catch (error) {
      setIsLoading(false);

      toast.fire({
        icon: "error",
        title: "Ocorreu um erro, tente novamente.",
      });
    }
  };

  const changeDateStatus = async (
    newStatus: SingleValue<{
      value: number;
      label: string;
    }>
  ) => {
    setDateStatusSelected(newStatus as Omit<StatusOption, "statusColor">);

    try {
      await api.patch(`/Quote/${quote.id}/procedureDateStatus`, null, {
        params: {
          procedureDateStatus: newStatus?.value,
        },
      });

      setIsLoading(true);
      const response = await api.get(`/Quote/${quoteId}`);
      await getQuotePendingItems(quoteId);

      setIsLoading(false);
      setQuote(response.data);

      toast.fire({
        icon: "success",
        title: "Status da data do procedimento atualizado com sucesso!",
      });
    } catch (error) {
      setIsLoading(false);

      toast.fire({
        icon: "error",
        title: "Ocorreu um erro, tente novamente.",
      });
    }
  };

  const changePaymentStatus = async (
    newStatus: SingleValue<{
      value: number;
      label: string;
    }>
  ) => {
    setPaymentStatusSelected(newStatus as Omit<StatusOption, "statusColor">);

    const body = {
      status: newStatus?.value,
      payment_date: moment().toISOString(true),
    };

    try {
      await api.patch(`/Quote/${quote.id}/Payment`, body);

      setIsLoading(true);
      const response = await api.get(`/Quote/${quoteId}`);
      await getQuotePendingItems(quoteId);

      setIsLoading(false);
      setQuote(response.data);

      toast.fire({
        icon: "success",
        title: "Status do pagamento atualizado com sucesso!",
      });
    } catch (error) {
      setIsLoading(false);

      toast.fire({
        icon: "error",
        title: "Ocorreu um erro, tente novamente.",
      });
    }
  };

  const getQuote = async (quoteId: number) => {
    setIsLoading(true);

    try {
      const { data }: AxiosResponse<Quote> = await api.get(`/Quote/${quoteId}`);

      setQuote(data);
      setQuoteStatusSelected({
        value: data.quoteStatus,
        label: quoteStatusTitle.get(data.quoteStatus) as string,
        statusColor: `var(--${quoteStatusColor.get(data.quoteStatus)})`,
      });
      setDateStatusSelected({
        value: data.dateStatus,
        label: ProcedureDateStatusDescription[data.dateStatus],
      });
      setPaymentStatusSelected({
        value: data.payment.status,
        label: QuotePaymentStatusDescription[data.payment.status],
      });

      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  const getAttachment = async (quoteId: number) => {
    setIsLoading(true);
    try {
      const { data }: AxiosResponse<FileAttachment> = await api.get(
        "/FileAttachment/find",
        {
          params: {
            idOrcamento: quoteId,
          },
        }
      );

      setDatabaseFiles(data.listFiles);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setFormQuote({
      ...formQuote,
      form: { type: selectedFormTypeOption.value },
    } as FormQuote);
  }, [selectedFormTypeOption]);

  useEffect(() => {
    const formType = formQuoteForm.map((o) => o.form.type);

    if (!formType.includes(FormTypeDescription["Financiamento Solicitante"])) {
      setSelectFinancingOptions(formTypeOptions);
      setSelectedFormTypeOption(defaultFormTypeOption);
      return;
    }

    setSelectFinancingOptions([
      {
        value: FormTypeDescription["Financiamento Avalista"],
        label: FormTypeDescription[1],
      },
    ]);
    setSelectedFormTypeOption({
      value: FormTypeDescription["Financiamento Avalista"],
      label: FormTypeDescription[1],
    });
  }, [formQuoteForm]);

  useEffect(() => {
    (async () => {
      getQuote(quoteId);
      getAttachment(quoteId);
      getFormQuote(quoteId);
    })();
  }, []);

  // Carrega as tabelas de máquina física para ocultar o botão de link nesses casos.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/Quote/TerminalPDQ");
        if (Array.isArray(data)) {
          setMachineTableIds(data.map((m: { id: number }) => m.id));
        }
      } catch {
        // Falha silenciosa: na dúvida, mantém o comportamento atual (backend já bloqueia a geração).
      }
    })();
  }, []);

  // Orçamento de máquina física (TerminalPDQ) não exibe o botão de link de pagamento.
  const paymentTableId = quote?.payment?.pricetable?.value;
  const isMachineQuote =
    paymentTableId != null && machineTableIds.includes(paymentTableId);

  return (
    <>
      {!!isLoading && (
        <ContainerAnimation className="content">
          <LoadingScreen />
        </ContainerAnimation>
      )}

      {!isLoading && (
        <ContainerAnimation className="content">
          {/* Cabeçalho */}
          <S.PageHeader>
            <div className="container">
              <S.Breadcrumb aria-label="Navegação">
                <Link
                  to={
                    userRole === RoleDescription.Patient
                      ? "/orcamentos-paciente"
                      : "/orcamentos-profissional"
                  }
                >
                  Orçamentos
                </Link>
                <span aria-hidden="true">›</span>
                <span aria-current="page">#{quote?.id}</span>
              </S.Breadcrumb>

              <S.HeaderRow>
                <S.PageTitle>
                  <S.BackButton
                    onClick={() => navigate(-1)}
                    aria-label="Voltar para orçamentos"
                    title="Voltar"
                  >
                    <i className="uil uil-arrow-left" aria-hidden="true" />
                  </S.BackButton>
                  Orçamento&nbsp;
                  <span style={{ color: "#9ca3af", fontWeight: 500 }}>
                    #{quote?.id}
                  </span>
                </S.PageTitle>

                <S.HeaderActions>
                  {quote?.actions?.includes(QuoteActions.EditStatus) && (
                    <ReactSelect
                      styles={selectorQuoteStatusCustomStyles(quoteStatusSelected)}
                      options={groupedOptions}
                      value={quoteStatusSelected}
                      onChange={changeQuoteStatus}
                      isSearchable={false}
                      formatGroupLabel={FormatGroupLabel}
                      noOptionsMessage={() => null}
                      aria-label="Alterar status do orçamento"
                    />
                  )}

                  {!!quote?.hasPaymentLink &&
                    !isMachineQuote &&
                    (quote?.payment?.linkAction === PaymentLinkAction.Copy ||
                      quote?.payment?.linkAction === PaymentLinkAction.CopyPay) && (
                      <Button
                        className="btn-sm btn-outline-info"
                        onClick={() => setPaymentLinkModalShow(true)}
                        aria-label="Abrir link de pagamento"
                      >
                        <i className="uil uil-link-add" aria-hidden="true" />
                        <span className="d-none d-sm-inline ms-1">
                          Link de Pagamento
                        </span>
                      </Button>
                    )}

                  {quote?.actions?.includes(QuoteActions.Edit) &&
                    userRole === RoleDescription.Admin && (
                      <Button
                        className="btn-sm btn-outline-primary"
                        onClick={() => setFormModalShow(true)}
                        aria-label="Editar orçamento"
                      >
                        <i className="uil uil-edit-alt" aria-hidden="true" />
                        <span className="d-none d-sm-inline ms-1">
                          Editar
                        </span>
                      </Button>
                    )}
                </S.HeaderActions>
              </S.HeaderRow>
            </div>
          </S.PageHeader>

          <S.Stepper aria-label="Status do orçamento">
            <div className="container">
              <S.StepperList>
                <TimeLineStep
                  step={QuoteStatus.Quote}
                  quoteStatus={quote?.quoteStatus}
                />

                <S.StepConnector $filled={(quote?.quoteStatus ?? 0) > QuoteStatus.Quote} />

                {quote?.quoteStatus === QuoteStatus.Analyzing ? (
                  <TimeLineStep
                    step={QuoteStatus.Analyzing}
                    quoteStatus={quote?.quoteStatus}
                    isPendingItems={!!pendingItems?.length}
                  />
                ) : (
                  <TimeLineStep
                    step={QuoteStatus.WaitingApproval}
                    quoteStatus={quote?.quoteStatus}
                    paymentMethod={quote?.payment?.method}
                    isPendingItems={!!pendingItems?.length}
                  />
                )}

                <S.StepConnector $filled={(quote?.quoteStatus ?? 0) >= QuoteStatus.Approved} />

                <TimeLineStep
                  step={QuoteStatus.Approved}
                  quoteStatus={quote?.quoteStatus}
                />

                <S.StepConnector $filled={(quote?.quoteStatus ?? 0) > QuoteStatus.Approved} />

                <TimeLineStep
                  step={QuoteStatus.Schedule}
                  quoteStatus={quote?.quoteStatus}
                />

                <S.StepConnector $filled={(quote?.quoteStatus ?? 0) > QuoteStatus.Schedule} />

                <TimeLineStep
                  step={QuoteStatus.Finished}
                  quoteStatus={quote?.quoteStatus}
                />
              </S.StepperList>
            </div>
          </S.Stepper>

          <div className="container">
            {/* Informações da mensagem se disponível */}
            {!!quote.infoMessage && (
              <p className="mt-5">
                <i className="uil uil-info-circle align-bottom display-6 me-1"></i>

                <span>{quote.infoMessage}</span>
              </p>
            )}

            {/* Lista de Pendências */}
            {!!pendingItems?.length && (
              <S.PendingAlert role="alert" aria-label="Pendências do orçamento">
                <div className="pending-header">
                  <i className="uil uil-exclamation-circle" aria-hidden="true" />
                  {pendingItems.length === 1
                    ? "1 pendência encontrada"
                    : `${pendingItems.length} pendências encontradas`}
                </div>
                <ul>
                  {pendingItems.map((pendingItem, index) => (
                    <li key={index}>
                      <span>{pendingItem.description}</span>
                      {quote?.actions?.includes(pendingItem.action) && (
                        <Link
                          className="btn btn-sm btn-outline-danger"
                          to={`/termos/${quote?.code}`}
                        >
                          Ver Termos
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </S.PendingAlert>
            )}

            {/* Informações do Orçamento */}
            <div className="row mt-3">
              <div className="col-12 col-lg-6 col-xl-7 pb-4 pb-md-0">

                {/* ── Partes ── */}
                <S.InfoCard>
                  <S.InfoCardHeader>
                    <S.InfoCardTitle>Partes</S.InfoCardTitle>
                    <small style={{ color: "#9ca3af", fontSize: "0.75rem" }}>
                      Criado em {moment(quote.creationDate).format("DD/MM/YYYY")}
                    </small>
                  </S.InfoCardHeader>
                  <S.InfoCardBody>
                    <S.InfoGrid>
                      <S.InfoItemFull>
                        <S.InfoLabel>Cliente</S.InfoLabel>
                        <S.InfoValue>{quote.patient?.name}</S.InfoValue>
                      </S.InfoItemFull>
                      <S.InfoItem>
                        <S.InfoLabel>Profissional</S.InfoLabel>
                        {quote.professional?.name ? (
                          <S.InfoValue>{quote.professional.name}</S.InfoValue>
                        ) : (
                          <S.InfoValueMuted>Não informado</S.InfoValueMuted>
                        )}
                      </S.InfoItem>
                      <S.InfoItem>
                        <S.InfoLabel>Instituição</S.InfoLabel>
                        <S.InfoValue>{quote.company?.name}</S.InfoValue>
                      </S.InfoItem>
                    </S.InfoGrid>
                  </S.InfoCardBody>
                </S.InfoCard>

                {/* ── Procedimento ── */}
                <S.InfoCard>
                  <S.InfoCardHeader>
                    <S.InfoCardTitle>Procedimento</S.InfoCardTitle>
                    {quote?.actions?.includes(QuoteActions.ModifyProcedureDateStatus) && (
                      <ReactSelect
                        options={dateStatusOptions}
                        value={dateStatusSelected}
                        isSearchable={false}
                        onChange={changeDateStatus}
                        styles={selectorStatusCustomStyles}
                        isOptionDisabled={(option) =>
                          option.value === dateStatusSelected.value
                        }
                        aria-label="Status da data do procedimento"
                      />
                    )}
                  </S.InfoCardHeader>
                  <S.InfoCardBody>
                    <S.ProcedureBox>
                      <S.ProcedureDescription>
                        {quote.description}
                      </S.ProcedureDescription>
                      <S.ProcedureDate>
                        <i className="uil uil-calendar-alt" aria-hidden="true" />
                        {moment(quote?.date).format("DD/MM/YYYY")}
                      </S.ProcedureDate>
                    </S.ProcedureBox>
                  </S.InfoCardBody>
                </S.InfoCard>

                {/* ── Pagamento ── */}
                <S.InfoCard>
                  <S.InfoCardHeader>
                    <S.InfoCardTitle>Pagamento</S.InfoCardTitle>
                    {quote?.actions?.includes(QuoteActions.ModifyPaymentStatus) && (
                      <ReactSelect
                        options={
                          quote.payment?.status === QuotePaymentStatusDescription.Pago
                            ? [
                                ...quotePaymentStatusOptions,
                                {
                                  value: QuotePaymentStatusDescription.Repasse,
                                  label: "Repasse ao prestador realizado",
                                },
                              ]
                            : quotePaymentStatusOptions
                        }
                        value={paymentStatusSelected}
                        isSearchable={false}
                        onChange={changePaymentStatus}
                        styles={selectorStatusCustomStyles}
                        isOptionDisabled={(option) =>
                          option.value === quote.payment.status
                        }
                        aria-label="Status do pagamento"
                      />
                    )}
                  </S.InfoCardHeader>
                  <S.InfoCardBody style={{ padding: 0 }}>
                    {quote.payment?.method === PaymentMethod.Financing &&
                      !!quote.payment?.finalValue && (
                        <div className="alert alert-success m-3 mb-0 py-2 small">
                          <i className="uil uil-check-circle me-1" aria-hidden="true" />
                          Proponente aprovado na 1ª fase. Aguardando avaliação documental.
                        </div>
                      )}

                    <S.PaymentMainValue>
                      <div className="label">Valor do Orçamento</div>
                      <div className="amount">{formatPrice(quote.payment?.value)}</div>
                      {quote.payment?.finalValue != null && (
                        <div className="total-amount">
                          <span className="total-label">Total c/ encargos:</span>
                          {formatPrice(quote.payment?.finalValue)}
                        </div>
                      )}
                    </S.PaymentMainValue>

                    <S.PaymentDetailsGrid>
                      <S.PaymentDetailItem>
                        <div className="pd-label">Método</div>
                        <div className="pd-value">
                          {PaymentMethodDescription[quote.payment?.method]}
                        </div>
                      </S.PaymentDetailItem>

                      {quote.payment?.parcelQtd != null && (
                        <S.PaymentDetailItem>
                          <div className="pd-label">Parcelas</div>
                          <div className="pd-value">{quote.payment.parcelQtd}×</div>
                        </S.PaymentDetailItem>
                      )}

                      {quote.payment?.parcelValue != null && (
                        <S.PaymentDetailItem>
                          <div className="pd-label">Valor da Parcela</div>
                          <div className="pd-value">
                            {formatPrice(quote.payment.parcelValue)}
                          </div>
                        </S.PaymentDetailItem>
                      )}

                      {quote.payment?.brand && (
                        <S.PaymentDetailItem>
                          <div className="pd-label">Bandeira</div>
                          <div className="pd-value">
                            <img
                              src={`${process.env.PUBLIC_URL}/bandeiras/${quote.payment.brand.toLowerCase()}.svg`}
                              alt={quote.payment.brand}
                              width={48}
                              style={{ display: "block" }}
                            />
                          </div>
                        </S.PaymentDetailItem>
                      )}

                      {quote.payment?.feeMonthly != null && (
                        <S.PaymentDetailItem>
                          <div className="pd-label">Taxa Mensal</div>
                          <div className="pd-value">
                            {formatAsPercent(quote.payment.feeMonthly)}
                          </div>
                        </S.PaymentDetailItem>
                      )}


                      {quote.payment?.cetCost != null && (
                        <S.PaymentDetailItem>
                          <div className="pd-label">Custo CET</div>
                          <div className="pd-value">
                            {formatAsPercent(quote.payment.cetCost)}
                          </div>
                        </S.PaymentDetailItem>
                      )}

                      <S.PaymentDetailItem>
                        <div className="pd-label">Resp. pela Taxa</div>
                        <div className="pd-value">
                          {PaymentFeeOwner[quote.payment?.paymentFeeOwner]}
                        </div>
                      </S.PaymentDetailItem>

                      <S.PaymentDetailItem>
                        <div className="pd-label">Data Limite</div>
                        {quote?.hasLimitDateAlert ? (
                          <div className="pd-value-danger">
                            <i className="uil uil-exclamation-triangle" aria-hidden="true" />
                            {moment(quote.limitDate).format("DD/MM/YYYY")}
                          </div>
                        ) : (
                          <div className="pd-value">
                            {moment(quote?.limitDate).format("DD/MM/YYYY")}
                          </div>
                        )}
                      </S.PaymentDetailItem>

                      {quote.payment?.paymentDate &&
                        moment(quote.payment.paymentDate).format("DD/MM/YYYY") !== "01/01/0001" && (
                          <S.PaymentDetailItem>
                            <div className="pd-label">Data Pagamento</div>
                            <div className="pd-value">
                              {moment(quote.payment.paymentDate).format("DD/MM/YYYY")}
                            </div>
                          </S.PaymentDetailItem>
                        )}

                      <S.PaymentDetailItem>
                        <div className="pd-label">Compensação</div>
                        <div className="pd-value">
                          {!quote.payment?.compensationValue
                            ? "—"
                            : formatPrice(quote.payment.compensationValue)}
                        </div>
                      </S.PaymentDetailItem>

                      <S.PaymentDetailItem>
                        <div className="pd-label">Data Compensação</div>
                        <div className="pd-value">
                          {!quote.payment?.paymentLiquidationDate
                            ? "—"
                            : moment(quote.payment.paymentLiquidationDate).format("DD/MM/YYYY")}
                        </div>
                      </S.PaymentDetailItem>
                    </S.PaymentDetailsGrid>
                  </S.InfoCardBody>
                </S.InfoCard>

              </div>

              {/* Chat do Orçamento */}
              {quote?.actions?.includes(QuoteActions.ViewQuoteChat) && (
                <Accordion
                  defaultActiveKey="0"
                  className="col-12 col-lg-6 col-xl-5 sticky-top h-50"
                  style={{ zIndex: 1 }}
                >
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>Chat</Accordion.Header>
                    <Accordion.Body className="p-0">
                      <Chat quoteId={quoteId} quoteActions={quote.actions} />
                    </Accordion.Body>
                  </Accordion.Item>
                  {quote?.company?.enableCheckList && (
                    <Accordion.Item eventKey="1">
                      <Accordion.Header>Checklist</Accordion.Header>
                      <Accordion.Body className="pb-2">
                        <Checklist quoteId={quoteId} />
                      </Accordion.Body>
                    </Accordion.Item>
                  )}
                  <Accordion.Item eventKey="2">
                    <Accordion.Header>Histórico</Accordion.Header>
                    <Accordion.Body className="p-0">
                      <AuditLogList quoteId={quoteId} />
                    </Accordion.Body>
                  </Accordion.Item>

                  {SHOW_SPLIT_PAGAMENTO && (
                  <Accordion.Item eventKey="3">
                    <Accordion.Header>
                      <i className="uil uil-share-alt me-2" />
                      Split de Pagamento
                    </Accordion.Header>
                    <Accordion.Body className="p-3">
                      <S.SplitWrap>
                        {splitItems.map((item, idx) => (
                          <S.SplitCard key={idx}>
                            <S.SplitCardHeader>
                              <S.SplitIndex aria-hidden="true">{idx + 1}</S.SplitIndex>
                              <S.SplitCardTitle>Beneficiário {idx + 1}</S.SplitCardTitle>
                              {splitItems.length > 1 && (
                                <S.SplitRemoveBtn
                                  type="button"
                                  aria-label={`Remover beneficiário ${idx + 1}`}
                                  onClick={() =>
                                    setSplitItems((prev) =>
                                      prev.filter((_, i) => i !== idx)
                                    )
                                  }
                                >
                                  <i className="uil uil-trash-alt" />
                                </S.SplitRemoveBtn>
                              )}
                            </S.SplitCardHeader>

                            <S.SplitFields>
                              <S.SplitFieldFull>
                                <S.SplitFieldLabel htmlFor={`split-name-${idx}`}>
                                  Nome do Beneficiário
                                </S.SplitFieldLabel>
                                <input
                                  id={`split-name-${idx}`}
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Nome completo"
                                  value={item.name}
                                  onChange={(e) =>
                                    setSplitItems((prev) =>
                                      prev.map((it, i) =>
                                        i === idx ? { ...it, name: e.target.value } : it
                                      )
                                    )
                                  }
                                />
                              </S.SplitFieldFull>

                              <div>
                                <S.SplitFieldLabel htmlFor={`split-pix-${idx}`}>
                                  Chave PIX
                                </S.SplitFieldLabel>
                                <input
                                  id={`split-pix-${idx}`}
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="CPF, e-mail, telefone ou EVP"
                                  value={item.pixKey}
                                  onChange={(e) =>
                                    setSplitItems((prev) =>
                                      prev.map((it, i) =>
                                        i === idx ? { ...it, pixKey: e.target.value } : it
                                      )
                                    )
                                  }
                                />
                              </div>

                              <div>
                                <S.SplitFieldLabel htmlFor={`split-value-${idx}`}>
                                  Valor
                                </S.SplitFieldLabel>
                                <div className="input-group input-group-sm">
                                  <span className="input-group-text">R$</span>
                                  <input
                                    id={`split-value-${idx}`}
                                    type="tel"
                                    className="form-control"
                                    placeholder="0,00"
                                    value={item.value}
                                    onChange={(e) =>
                                      setSplitItems((prev) =>
                                        prev.map((it, i) =>
                                          i === idx
                                            ? { ...it, value: formatSplitBRL(e.target.value) }
                                            : it
                                        )
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            </S.SplitFields>
                          </S.SplitCard>
                        ))}

                        <div className="d-flex flex-wrap gap-2">
                          <S.SplitAddBtn
                            type="button"
                            onClick={() =>
                              setSplitItems((prev) => [
                                ...prev,
                                { name: "", pixKey: "", value: "" },
                              ])
                            }
                          >
                            <i className="uil uil-plus" />
                            Adicionar beneficiário
                          </S.SplitAddBtn>

                          <S.SplitAddBtn
                            type="button"
                            onClick={() => {
                              const prefill = buildSplitFromCompany();
                              if (prefill) {
                                setSplitItems(prefill);
                                toast.fire({
                                  icon: "success",
                                  title: `${prefill.length} beneficiário(s) carregados do cadastro da empresa.`,
                                });
                              } else {
                                toast.fire({
                                  icon: "info",
                                  title:
                                    "Nenhum beneficiário pré-cadastrado para esta empresa. Cadastre em Instituições → Editar → Pagamento.",
                                });
                              }
                            }}
                          >
                            <i className="uil uil-building" />
                            Recarregar do cadastro da empresa
                          </S.SplitAddBtn>
                        </div>

                        <S.SplitTotalBar $valid={splitIsValid} $hasData={splitHasData}>
                          <S.SplitTotalTrack>
                            <S.SplitTotalFill
                              $pct={splitQuoteValue > 0 ? (splitTotal / splitQuoteValue) * 100 : 0}
                              $valid={splitIsValid}
                            />
                          </S.SplitTotalTrack>
                          <S.SplitTotalInfo>
                            <span className="label">Total distribuído</span>
                            <span className="values">
                              <span className="total">
                                R$ {splitTotal.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                              </span>
                              <span className="sep">/</span>
                              <span className="quote">
                                R$ {splitQuoteValue.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                              </span>
                              {splitIsValid ? (
                                <span className="status-ok">
                                  <i className="uil uil-check-circle" /> Correto
                                </span>
                              ) : (
                                <span className="status-diff">
                                  <i className="uil uil-exclamation-circle" /> Diferença de R${" "}
                                  {Math.abs(splitTotal - splitQuoteValue).toLocaleString("pt-BR", {
                                    minimumFractionDigits: 2,
                                  })}
                                </span>
                              )}
                            </span>
                          </S.SplitTotalInfo>
                        </S.SplitTotalBar>

                        {splitHasData && (
                          <S.SplitSaveRow>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              disabled={!splitIsValid}
                              onClick={() =>
                                toast.fire({ icon: "success", title: "Split salvo com sucesso!" })
                              }
                            >
                              <i className="uil uil-save me-1" />
                              Salvar Split
                            </button>
                          </S.SplitSaveRow>
                        )}
                      </S.SplitWrap>
                    </Accordion.Body>
                  </Accordion.Item>
                  )}
                </Accordion>
              )}

              {/* Informações Complementares */}
              <div className="col-12 col-lg-6 mb-4">
                <S.SectionCard>
                  <S.SectionCardHeader>
                    <S.SectionCardTitle>
                      <i className="uil uil-file-alt" aria-hidden="true" />
                      Informações Complementares
                    </S.SectionCardTitle>
                  </S.SectionCardHeader>
                  <S.SectionCardBody>
                    <div className="rounded-3 border px-3 pb-0 mb-3 overflow-files">
                      {!formQuoteForm.length ? (
                        <p className="my-3 text-muted small">Nenhum formulário encontrado</p>
                      ) : (
                        <>
                          {formQuoteForm.map((formQuote) => (
                            <FormQuoteCard
                              key={formQuote.id}
                              formQuote={formQuote}
                              quoteId={quoteId}
                            />
                          ))}
                        </>
                      )}
                    </div>
                    {quote?.actions?.includes(QuoteActions.AddExtraInfoForm) && (
                      <Accordion>
                        <Card>
                          <Card.Header className="text-end">
                            <CustomToggle
                              title="Adicionar Formulário"
                              eventKey="0"
                            />
                          </Card.Header>

                          <Accordion.Collapse eventKey="0">
                            <Card.Body>
                              <div className="pb-2">
                                <Select
                                  options={selectFormTypeOptions}
                                  onChange={(newValue) => {
                                    handleFinancingChange(
                                      newValue as { value: number; label: string }
                                    );
                                  }}
                                  placeholder="Selecione o Tipo do Financiamento"
                                  isClearable={true}
                                  value={selectedFormTypeOption}
                                  styles={customStyles}
                                  theme={customTheme}
                                />
                              </div>

                              <div className="text-end pt-4">
                                <Button
                                  className="btn-sm btn-primary"
                                  disabled={isLoading || !selectedFormTypeOption}
                                  onClick={() => setFormQuoteModalShow(true)}
                                >
                                  Adicionar
                                  {!!isLoading && (
                                    <CircularProgress
                                      color="inherit"
                                      size={12}
                                      className="align-text-top ms-2"
                                    />
                                  )}
                                </Button>
                              </div>
                            </Card.Body>
                          </Accordion.Collapse>
                        </Card>
                      </Accordion>
                    )}
                  </S.SectionCardBody>
                </S.SectionCard>
              </div>

              {/* Arquivos do Orçamento */}
              <div className="col-12 col-lg-6">
                <S.SectionCard>
                  <S.SectionCardHeader>
                    <S.SectionCardTitle>
                      <i className="uil uil-paperclip" aria-hidden="true" />
                      Arquivos do Orçamento
                    </S.SectionCardTitle>
                    {quote.payment?.method === PaymentMethod.Financing && (
                      <OverlayTrigger
                        placement="left"
                        overlay={
                          <Tooltip>
                            <RequiredDocuments />
                          </Tooltip>
                        }
                      >
                        <i
                          className="uil uil-info-circle"
                          style={{ color: "var(--text-muted)", cursor: "help", fontSize: "1.125rem" }}
                          aria-label="Documentos necessários para financiamento"
                        />
                      </OverlayTrigger>
                    )}
                  </S.SectionCardHeader>
                  <S.SectionCardBody>
                    <div className="rounded-3 border px-3 pb-0 mb-3 overflow-files">
                      {!databaseFiles.length ? (
                        <p className="my-3 text-muted small">Nenhum arquivo encontrado</p>
                      ) : (
                        <>
                          {databaseFiles.map((file) => (
                            <FileCard
                              key={file.id}
                              file={file}
                              setDatabaseFiles={setDatabaseFiles}
                              quoteId={quoteId}
                              quoteActions={quote.actions}
                            />
                          ))}
                        </>
                      )}
                    </div>

                    {quote?.actions?.includes(QuoteActions.AddAttachment) && (
                      <Accordion>
                        <Card>
                          <Card.Header className="text-end">
                            <CustomToggle
                              title="Adicionar Arquivos"
                              eventKey="0"
                            />
                          </Card.Header>

                          <Accordion.Collapse eventKey="0">
                            <Card.Body>
                              <FileAddForm
                                quoteId={quote.id}
                                setDatabaseFiles={setDatabaseFiles}
                              />
                            </Card.Body>
                          </Accordion.Collapse>
                        </Card>
                      </Accordion>
                    )}
                  </S.SectionCardBody>
                </S.SectionCard>
              </div>
            </div>
          </div>
        </ContainerAnimation>
      )}

      {/* Modal para editar orçamento */}
      <ModalComponent
        title="Editar Orçamento"
        show={formModalShow}
        onHide={() => setFormModalShow(false)}
      >
        {userRole === RoleDescription.Professional && (
          <ProfessionalQuoteEditForm
            onHide={() => setFormModalShow(false)}
            quote={quote}
            setQuote={setQuote}
          />
        )}

        {userRole === RoleDescription.Patient && (
          <PatientQuoteEditForm
            onHide={() => setFormModalShow(false)}
            quote={quote}
            setQuote={setQuote}
          />
        )}

        {userRole === RoleDescription.Admin && (
          <QuoteEditForm
            onHide={() => setFormModalShow(false)}
            quote={quote}
            setQuote={setQuote}
          />
        )}

        {userRole === RoleDescription.Secretary && (
          <SecretaryQuoteEditForm
            onHide={() => setFormModalShow(false)}
            quote={quote}
            setQuote={setQuote}
          />
        )}
      </ModalComponent>

      {/* Modal de Link de Pagamento */}
      <ModalComponent
        title="Link de Pagamento"
        show={paymentLinkModalShow}
        onHide={() => setPaymentLinkModalShow(false)}
        size="lg"
      >
        <PaymentLink
          onHide={() => setPaymentLinkModalShow(false)}
          quoteId={quote?.id}
          linkAction={quote?.payment?.linkAction}
        />
      </ModalComponent>

      {/* Modal de Envio de link de termos */}
      <ModalComponent
        title="Link de Termos"
        show={termsLinkModalShow}
        onHide={() => setTermsLinkModalShow(false)}
      >
        <TermsLink />
      </ModalComponent>

      {/* Modal FormQuote Stepper */}
      <ModalComponent
        title={`Adicionar Formulário do ${selectedFormTypeOption?.label}`}
        show={formQuoteModalShow}
        onHide={() => closeFormQuoteStepper()}
        size="xl"
      >
        <ModalBodyComponentStepper
          activeStep={activeStep}
          steps={[
            {
              label: "Informações Pessoais",
              component: (
                <Step1PersonalInfoForm
                  setActiveStep={setActiveStep}
                  formQuote={formQuote}
                  setFormQuote={setFormQuote}
                  onHide={() => setFormQuoteModalShow(false)}
                  quoteId={quoteId}
                  action={ActionFormQuote.Create}
                />
              ),
            },
            {
              label: "Endereço",
              component: (
                <Step2AddessInfoForm
                  setActiveStep={setActiveStep}
                  formQuote={formQuote}
                  setFormQuote={setFormQuote}
                  onHide={() => setFormQuoteModalShow(false)}
                  quoteId={quoteId}
                  action={ActionFormQuote.Create}
                />
              ),
            },
            {
              label: "Dados Profissionais",
              component: (
                <Step3ProfessionalInfoForm
                  setActiveStep={setActiveStep}
                  formQuote={formQuote}
                  setFormQuote={setFormQuote}
                  onHide={() => setFormQuoteModalShow(false)}
                  quoteId={quoteId}
                  action={ActionFormQuote.Create}
                />
              ),
            },
            {
              label: "Outros",
              component: (
                <Step4OthersInfoForm
                  setActiveStep={setActiveStep}
                  formQuote={formQuote}
                  setFormQuote={setFormQuote}
                  onHide={() => setFormQuoteModalShow(false)}
                  quoteId={quoteId}
                  action={ActionFormQuote.Create}
                />
              ),
            },
          ]}
        />
      </ModalComponent>
    </>
  );
};

export default QuoteDetails;
